import {DateTime, Interval} from "luxon";
import {inject, injectable} from "tsyringe";
import {transformer} from "../decorators/transformer.decorator";
import {EmployeeToIssueAssignmentDto} from "../domain/dtos/employee-to-issue-assignment.dto";
import {IssueDTO} from "../domain/dtos/issue.dto";
import {Issue} from "../domain/entities/issue.entity";
import {EmployeeRepository} from "../domain/repositories/employee.repository";
import {IssueStatusRepository} from "../domain/repositories/issue-status.repository";
import {IssueTypeRepository} from "../domain/repositories/issue-type.repository";
import {KeywordRepository} from "../domain/repositories/keyword.repository";
import {ProjectRepository} from "../domain/repositories/project.repository";
import {DateHelper} from "../helpers/date.helper";
import {AbstractDtoToEntityTransformer} from "./abstract-dto-to-entity.transformer";
import {EmployeeToIssueAssignmentRepository} from "../domain/repositories/employee-to-issue-assignment.repository";

@injectable()
@transformer({
    source: `dto`,
    target: Issue
})
export class DtoToIssueTransformer extends AbstractDtoToEntityTransformer<IssueDTO, Issue> {

    constructor(
        @inject(ProjectRepository) private readonly projectRepository: ProjectRepository,
        @inject(IssueTypeRepository) private readonly issueTypeRepository: IssueTypeRepository,
        @inject(IssueStatusRepository) private readonly issueStatusRepository: IssueStatusRepository,
        @inject(EmployeeRepository) private readonly employeeRepository: EmployeeRepository,
        @inject(KeywordRepository) private readonly keywordRepository: KeywordRepository,
        @inject(EmployeeToIssueAssignmentRepository) private readonly employeeToIssueAssignmentRepository: EmployeeToIssueAssignmentRepository,
    ) {
        super();
    }

    /**
     * Transform DTO to Issue entity.
     * All repository lookups are parallelized to enable request batching.
     */
    protected async toEntity(dto: IssueDTO): Promise<Issue> {

        // Parallel fetch all related entities to enable batching via debounceTime
        const [project, type, status, creator, keywords] = await Promise.all([
            dto.project ? this.projectRepository.findOneByIdOrDto(dto.project) : Promise.resolve(null),
            this.issueTypeRepository.findOneByIdOrDto(dto.type),
            // The status may be null (statusless types lose it in the issue-type data migration).
            dto.status ? this.issueStatusRepository.findOneByIdOrDto(dto.status) : Promise.resolve(null),
            dto.creator ? this.employeeRepository.findOneByIdOrDto(dto.creator) : Promise.resolve(null),
            Promise.all(dto.keywords.map(keyword => this.keywordRepository.findOneByIdOrDto(keyword)))
        ]);

        if (!type) {
            throw new Error(`No type with uid ${dto.type}`);
        }

        if (dto.status && !status) {
            throw new Error(`No status with uid ${dto.status}`);
        }

        const issue = this.entityManager.getLoadedEntity(dto.uid) as Issue ?? new Issue(type, status);

        // Transform employeeToIssueAssignments and extract employees. On save, the dialog
        // derives the same list via IssueEmployeesHelper (issue-command-builder.service.ts).
        // IssueDTO declares assignment DTOs or uid strings here, but the backend
        // (Issue::jsonSerialize and Issue::customizedJsonSerialize) always serialises the
        // assignment entities themselves, and this transformer only reads backend responses.
        const assignments = (dto.employeeToIssueAssignments as EmployeeToIssueAssignmentDto[]).map((assignmentDto: EmployeeToIssueAssignmentDto) => this.employeeToIssueAssignmentRepository.fromDto(assignmentDto));
        const employees = await Promise.all(assignments.map(async (assignment: any) => {
            const employee = await assignment.employee;
            return employee;
        }));

        // An issue that is already loaded is reused, so type and status are set here as well; the
        // constructor alone would leave a reloaded issue with the type and status it was first
        // loaded with.
        Object.assign(issue,
            {
                type,
                status,
                title: dto.title,
                timeRange: Interval.fromDateTimes(
                    DateHelper.fromSQL(dto.start),
                    DateHelper.fromSQL(dto.end)
                ),
                description: dto.description,
                employeeToIssueAssignments: assignments,
                employees: employees.filter(e => e != null),
                creator,
                keywords,
                project,
                deletedAt: !!dto.deletedAt ? DateTime.fromSQL(dto.deletedAt) : null,
            }
        );
        issue.commit();

        return issue;
    }

}